import { AreaCategory, StudioTimeSlotStatus, TimeSlotWarningText } from 'fitbeat.gym-core';
import React, { FunctionComponent } from 'react';
import {
  StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { connect } from 'react-redux';

import CaretIcon from '../../assets/icons/caret-icon.svg';
import WarningIcon from '../../assets/icons/warning-icon.svg';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import {
  getAreaCategoryDescription, getAreaDisplayName, getAreaIcon,
} from '../../utils/areaCategoryMapping';
import { TranslateProp } from '../../common-types';
import { areaStatusSelector, areaWarningSelector } from '../selectors';
import { IAppStore } from '../../HomeWorkouts';

interface IProps {
  t: TranslateProp,
  title: AreaCategory,
  index: number,
  isActive: boolean,
  setActiveAccordionIndex: (index: number) => void
}

interface IMapStateProps {
  areaStatus?: StudioTimeSlotStatus,
  areaWarning?: TimeSlotWarningText
}

const AreaCardHeader: FunctionComponent<IProps & IMapStateProps> = ({
  t, title, index, isActive, areaStatus, areaWarning, setActiveAccordionIndex,
}) => {
  const isAvailable = areaStatus === 'Available';
  const areaIcon = getAreaIcon(title);
  const caretColor = isAvailable ? Theme.PRIMARY : Theme.GREY_LIGHT;
  const caretRotation = isActive ? '180deg' : '0deg';

  const renderStatusBadge = () => {
    if (isAvailable) {
      return;
    }

    return (
      <View style={styles.badge}>
        <Text style={styles.badgeText}>
          {areaStatus}
        </Text>
      </View>
    );
  };

  return (
    <TouchableOpacity
      disabled={!isAvailable}
      onPress={() => {
        if (isActive) {
          setActiveAccordionIndex(-1);
        } else {
          setActiveAccordionIndex(index);
        }
      }}
    >
      <View style={styles.headerContainer}>
        <View style={styles.headingContainer}>
          <View style={styles.iconTitleContainer}>
            {areaIcon && (
              <View style={styles.iconContainer}>
                {areaIcon}
              </View>
            )}
            <Text style={styles.areaTitle}>{getAreaDisplayName(title)}</Text>
          </View>

          <View style={styles.statusCaretContainer}>
            {renderStatusBadge()}
            <View style={styles.caretIconContainer}>
              <CaretIcon
                style={{
                  transform: [{ rotate: caretRotation }],
                }}
                stroke={caretColor}
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </View>
          </View>
        </View>
        <Text style={styles.areaSubTitle}>{getAreaCategoryDescription(title, t)}</Text>
        {areaWarning && (
          <View style={styles.warningContainer}>
            <WarningIcon />
            <Text style={styles.warningText}>
              {t(areaWarning, { areaCategory: getAreaDisplayName(title) })}
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    paddingVertical: defaultScale(15),
    backgroundColor: Theme.WHITE,
    marginTop: defaultScale(5),
  },
  headingContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: defaultScale(5),
    paddingLeft: defaultScale(15),
  },
  iconTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    width: defaultScale(30),
    height: defaultScale(30),
    marginRight: defaultScale(5),
  },
  areaTitle: {
    ...Theme.textStyle.bodyCopyBold,
    textTransform: 'capitalize',
  },
  areaSubTitle: {
    fontSize: defaultScale(12),
    fontWeight: '500',
    color: Theme.GREY_MEDIUM,
    paddingLeft: defaultScale(15),
  },
  statusCaretContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  caretIconContainer: {
    marginLeft: defaultScale(5),
    marginRight: defaultScale(15),
  },
  badge: {
    backgroundColor: Theme.GREY_LIGHT,
    opacity: 0.6,
    paddingVertical: defaultScale(5),
    paddingHorizontal: defaultScale(10),
    borderRadius: defaultScale(50),
  },
  badgeText: {
    ...Theme.textStyle.smallBold,
    textTransform: 'uppercase',
    color: Theme.GREY_MEDIUM,
  },
  warningContainer: {
    padding: defaultScale(10),
    borderRadius: defaultScale(10),
    backgroundColor: Theme.GREY_FIGMA,
    display: 'flex',
    flexDirection: 'row',
    marginTop: defaultScale(10),
    marginHorizontal: defaultScale(15),
    alignItems: 'center',
  },
  warningText: {
    ...Theme.textStyle.small,
    flex: 1,
    color: Theme.GREY_DARK_MEDIUM_DARK,
    marginLeft: defaultScale(10),
  },
});

const mapStateToProps = (state: IAppStore, ownProps: IProps): IMapStateProps => ({
  areaStatus: areaStatusSelector(state, ownProps.title),
  areaWarning: areaWarningSelector(state, ownProps.title),
});

export default connect(mapStateToProps)(AreaCardHeader);
